import { c as _c } from "react/compiler-runtime";
import { ArrowRight } from "lucide-react";
import type { PropsWithChildren } from "react";
import { isValidUrl } from "common/lib/isValidUrl";
import { CallToActionStyle } from "common/types/graphql";
import { Heading } from "common/components/Heading";
import clsx from "classnames";
import { Button } from "common/components/Button";
import { useHasBeenInViewport } from "common/hooks/interaction/useHasBeenInViewport";
import { useEffect } from "react";
import { CallToActionTrackingEventType, trackCallToActionEvent } from "modules/promos/lib/tracking";
import { trackCallToActionEngaged, trackCallToActionViewed } from "common/lib/tcEventCollector";
import { useTrackLinkClick } from "common/hooks/interaction/useTrackLinkClick";
export interface CallToActionProps {
  href: string;
  title?: string;
  text: string;
  style: CallToActionStyle;
  analyticsId: string;
}
export const CallToAction = t0 => {
  "use memo";

  const $ = _c(30);
  const {
    href,
    title,
    text,
    style,
    analyticsId
  } = t0;
  const sanitizedHref = isValidUrl(href) ? href : undefined;
  const {
    setRef,
    hasBeenInViewport
  } = useHasBeenInViewport(0);
  let t1;
  let t2;
  if ($[0] !== analyticsId || $[1] !== hasBeenInViewport) {
    t1 = () => {
      if (analyticsId && analyticsId.length > 0 && hasBeenInViewport) {
        trackCallToActionEvent(CallToActionTrackingEventType.Impression, analyticsId);
        trackCallToActionViewed(analyticsId);
      }
    };
    t2 = [hasBeenInViewport, analyticsId];
    $[0] = analyticsId;
    $[1] = hasBeenInViewport;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== analyticsId) {
    t3 = () => {
      trackCallToActionEvent(CallToActionTrackingEventType.Engagement, analyticsId);
      trackCallToActionEngaged(analyticsId);
    };
    $[4] = analyticsId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const trackClick = useTrackLinkClick(href, analyticsId, t3);
  if (title) {
    const t4 = style === CallToActionStyle.Default;
    const t5 = style === CallToActionStyle.Donate;
    const t6 = style === CallToActionStyle.Newsletter;
    let t7;
    if ($[6] !== t4 || $[7] !== t5 || $[8] !== t6) {
      t7 = clsx("my-6 flex flex-col items-center gap-6 px-6 py-8 text-center", {
        "bg-gray-50 text-black": t4,
        "bg-red-500 text-white": t5,
        "bg-indigo-600 text-white": t6
      });
      $[6] = t4;
      $[7] = t5;
      $[8] = t6;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    let t8;
    if ($[10] !== title) {
      t8 = <Heading level={3} styledAsLevel={4}>{title}</Heading>;
      $[10] = title;
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    const t9 = style !== CallToActionStyle.Default;
    let t10;
    if ($[12] !== href || $[13] !== t9 || $[14] !== text || $[15] !== trackClick) {
      t10 = <Button size="large" onDark={t9} primary={true} href={href} onClick={trackClick} onAuxClick={trackClick}>{text}</Button>;
      $[12] = href;
      $[13] = t9;
      $[14] = text;
      $[15] = trackClick;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    let t11;
    if ($[17] !== setRef || $[18] !== t10 || $[19] !== t7 || $[20] !== t8) {
      t11 = <div className={t7} ref={setRef}>{t8}{t10}</div>;
      $[17] = setRef;
      $[18] = t10;
      $[19] = t7;
      $[20] = t8;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    return t11;
  }
  let t4;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ArrowRight className="inline size-5 flex-none" />;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  let t5;
  if ($[23] !== sanitizedHref || $[24] !== text || $[25] !== trackClick) {
    t5 = <a href={sanitizedHref} className="inline-flex gap-3 text-sm font-bold underline hover:text-indigo-600" onClick={trackClick} onAuxClick={trackClick}>{text} {t4}</a>;
    $[23] = sanitizedHref;
    $[24] = text;
    $[25] = trackClick;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== setRef || $[28] !== t5) {
    t6 = <div className="my-4" ref={setRef}>{t5}</div>;
    $[27] = setRef;
    $[28] = t5;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  return t6;
};
export type CallToActionComponent = typeof CallToAction;